import { GoogleGenAI, Type } from "@google/genai";
import { Chapter, Character, MuseMessage, WorldItem, CritiqueItem, MuseContextType, BookProject } from "../types";

// Use stable model for validation and simple tasks, and as fallback
const MODEL_FAST = "gemini-3-flash-preview"; 
// Use smart model for complex creative writing
const MODEL_SMART = "gemini-3-pro-preview"; 

// Local storage keys
const STORAGE_KEY = 'bookwiz_api_key';
const CACHE_KEY = 'bookwiz_api_cache';

// Initialize key from local storage or environment variable
let currentApiKey = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) || process.env.API_KEY || '' : process.env.API_KEY || '';

// --- CACHING SERVICE ---
class RequestCache {
  private cache: Record<string, any>;

  constructor() {
    try {
      const stored = localStorage.getItem(CACHE_KEY);
      this.cache = stored ? JSON.parse(stored) : {};
    } catch (e) {
      this.cache = {};
    }
  }

  // Create a hash from the input prompt/params
  private hash(key: string): string {
    let hash = 0, i, chr;
    if (key.length === 0) return hash.toString();
    for (i = 0; i < key.length; i++) {
      chr = key.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return hash.toString();
  }

  get(key: string): any | null {
    const h = this.hash(key);
    return this.cache[h] || null;
  }

  set(key: string, value: any) {
    const h = this.hash(key);
    this.cache[h] = value;
    try {
      // Basic pruning if too large (approx 2MB limit check is hard, so we just slice keys if too many)
      const keys = Object.keys(this.cache);
      if (keys.length > 500) {
        delete this.cache[keys[0]];
      }
      localStorage.setItem(CACHE_KEY, JSON.stringify(this.cache));
    } catch (e) {
      console.warn("Cache write failed (likely full)", e);
    }
  }
}

const apiCache = new RequestCache();


export const setApiKey = (key: string | null) => {
  if (key) {
    currentApiKey = key;
    localStorage.setItem(STORAGE_KEY, key);
  } else {
    currentApiKey = '';
    localStorage.removeItem(STORAGE_KEY);
  }
};

export const hasApiKey = (): boolean => {
  return !!currentApiKey;
};

export const getApiKey = (): string => {
  return currentApiKey;
};

// --- HELPER: Parse JSON safely ---
const parseResponseJSON = (text: string | undefined) => {
  if (!text) throw new Error("Empty response from AI");
  try {
    // 1. Remove markdown code blocks (```json ... ```)
    let cleaned = text.replace(/```json\s*|\s*```/g, "").trim();
    // 2. Sometimes it returns markdown without json tag
    cleaned = cleaned.replace(/```\s*|\s*```/g, "").trim();
    return JSON.parse(cleaned);
  } catch (e) {
    console.error("Failed to parse JSON:", text);
    throw new Error("AI response was not valid JSON. Please try again.");
  }
};

// Retry helper
const generateContentWithRetry = async (ai: GoogleGenAI, model: string, params: any) => {
  const maxRetries = 3;
  let lastError;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await ai.models.generateContent({
        model,
        ...params
      });
    } catch (e: any) {
      lastError = e;
      const msg = (e.message || e.toString()).toLowerCase();
      
      // Check for rate limits (429), quota issues, or server overloads (503)
      if (msg.includes('429') || msg.includes('quota') || msg.includes('resource_exhausted') || msg.includes('overloaded') || msg.includes('503')) {
        console.warn(`Gemini API Retry ${i+1}/${maxRetries} due to: ${msg}`);
        // Exponential backoff: 2s, 4s, 8s + jitter
        const delay = Math.pow(2, i + 1) * 1000 + (Math.random() * 1000);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      // If not retryable, throw immediately
      throw e;
    }
  }
  throw lastError;
};

// Wrapper for caching calls
const generateWithCache = async (
  ai: GoogleGenAI, 
  model: string, 
  params: any, 
  useCache = true
) => {
  const cacheKey = JSON.stringify({ model, params });
  
  if (useCache) {
    const cached = apiCache.get(cacheKey);
    if (cached) {
      console.log("Serving from Cache:", model);
      return cached;
    }
  }

  const response = await generateContentWithRetry(ai, model, params);
  
  // Only cache if successful text
  if (response && response.text) {
    apiCache.set(cacheKey, response);
  }
  
  return response;
};

export const validateApiKey = async (key: string): Promise<boolean> => {
  // Basic heuristic: Google API keys are typically around 39 characters.
  if (!key || key.length < 20) return false;

  try {
    const ai = new GoogleGenAI({ apiKey: key });
    // Don't cache validation checks
    const response = await generateContentWithRetry(ai, MODEL_FAST, {
      contents: "Reply with the word ok", 
    });
    return !!(response.text && response.text.length > 0);
  } catch (error: any) {
    const msg = (error.message || error.toString()).toLowerCase();
    if (msg.includes('429') || msg.includes('quota') || msg.includes('resource_exhausted') || msg.includes('503') || msg.includes('overloaded')) {
       console.warn("API Key Validation: Rate limited but authenticated. Allowing entry.");
       return true;
    }
    console.error("API Key Validation Failed:", error);
    return false;
  }
};

const getClient = () => {
  if (!currentApiKey) {
    throw new Error("API Key is missing. Please ensure you have entered a verified API Key.");
  }
  return new GoogleGenAI({ apiKey: currentApiKey });
};

export const refinePremise = async (
  premise: string,
  genre: string,
  style: string,
  language: string
): Promise<string> => {
  const ai = getClient();
  
  const prompt = `
    You are a professional book editor. 
    Refine the following book idea into a compelling, award-winning quality premise/blurb.
    
    Original Idea: "${premise}"
    Genre: ${genre}
    Desired Style: ${style}
    Language: ${language}
    
    Instructions:
    - Keep it under 150 words.
    - Make it punchy, intriguing, and professional.
    - Fix any grammar issues.
    - Do NOT add a title, just the premise text.
  `;

  const response = await generateWithCache(ai, MODEL_FAST, {
    contents: prompt,
  });

  return response.text ? response.text.trim() : premise;
};

export const generateBookOutline = async (
  title: string | null,
  genre: string,
  style: string,
  language: string,
  premise: string,
  targetWordCount: number,
  referenceMaterials: string[] = [],
  worldSetting?: string
): Promise<{ title: string; synopsis: string; styleGuide: string; chapters: any[] }> => {
  const ai = getClient();

  const hasReferences = referenceMaterials.length > 0;

  const systemInstruction = `You are a professional editor and author. Your goal is to structure a masterpiece. 
  Create a detailed chapter outline. The structure should be compelling, with good pacing and emotional depth.
  IMPORTANT: Write all output in ${language}.`;

  let referencePromptSection = "";
  if (hasReferences) {
    // Truncate references to avoid context limit issues
    const charLimit = referenceMaterials.length > 1 ? 40000 : 80000;
    
    referencePromptSection = `
    --- REFERENCE MATERIALS PROVIDED ---
    The user has provided ${referenceMaterials.length} reference texts/scripts.
    
    CRITICAL INSTRUCTIONS FOR REFERENCES:
    1. **Style & Tone:** Analyze the literary voice, sentence structure, and atmospheric quality of the provided references.
    2. **Content & Knowledge Extraction:** If the uploaded text is non-fiction (e.g., psychology, history, science) or source material (scripts, lore), you MUST extract key concepts, facts, theories, or plot beats from it.
    3. **Synthesis:** Blend the *style* of the references with the *knowledge* from the uploaded files.
    4. **Unique Creation:** Do NOT just copy text. Use the information to create a unique, new manner of storytelling.
    5. **Style Guide Generation:** Ensure the "styleGuide" field you generate explicitly mentions how to incorporate this specific knowledge/style into the prose.
    
    REFERENCE CONTENT (Truncated):
    ${referenceMaterials.map((ref, i) => `[REFERENCE ${i+1}]:\n${ref.slice(0, charLimit)}...`).join('\n\n')}
    ------------------------------------
    `;
  }

  const prompt = `
    Project Details:
    - Genre: ${genre}
    - Style: ${style}
    - Premise: ${premise}
    - Language: ${language}
    - Target Book Length: Approximately ${targetWordCount} words.
    ${title ? `- Working Title: ${title}` : '- Please suggest a title.'}
    ${worldSetting ? `- WORLD SETTING/CONTEXT: ${worldSetting}` : ''}

    ${referencePromptSection}

    Generate a JSON response containing:
    1. A refined, catchy Title (in ${language}).
    2. A detailed Synopsis (2-3 paragraphs, in ${language}). **Ensure this synopsis integrates the concepts/information found in the uploaded reference materials.**
    3. A "styleGuide" string: A detailed instruction paragraph describing the specific literary voice, tone, and **key informational themes (e.g., specific psychology concepts)** derived from the references that the AI should follow when writing the prose later.
    4. A comprehensive list of Chapters appropriate for a ${targetWordCount}-word book.
       - For ~10,000 words or less (Short Story), generate 8-12 chapters.
       - For ~20,000-40,000 words (Novella), generate 15-20 chapters.
       - For ~50,000-80,000 words (Novel), generate 25-35 chapters.
       - For ~100,000+ words (Epic), generate 40-60 chapters.
       
       Each chapter must have a 'title' and a detailed 'summary' of what happens in that chapter (plot points, emotional beats).
  `;

  const config = {
    systemInstruction,
    responseMimeType: "application/json",
    responseSchema: {
      type: Type.OBJECT,
      properties: {
        title: { type: Type.STRING },
        synopsis: { type: Type.STRING },
        styleGuide: { type: Type.STRING },
        chapters: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              summary: { type: Type.STRING },
            },
            required: ["title", "summary"]
          },
        },
      },
      required: ["title", "synopsis", "chapters", "styleGuide"]
    },
  };

  try {
    // Attempt 1: Smart Model
    const response = await generateWithCache(ai, MODEL_SMART, { contents: prompt, config });
    return parseResponseJSON(response.text);
  } catch (error: any) {
    console.warn("Smart Outline Generation failed, attempting Fallback (Flash)...", error);
    try {
      // Attempt 2: Fast Model Fallback
      const response = await generateWithCache(ai, MODEL_FAST, { contents: prompt, config });
      return parseResponseJSON(response.text);
    } catch (fallbackError) {
      console.error("Outline generation completely failed:", fallbackError);
      throw fallbackError;
    }
  }
};

export const generateCharacters = async (
  synopsis: string,
  style: string,
  language: string,
  worldSetting?: string
): Promise<Character[]> => {
  const ai = getClient();

  const prompt = `
    Based on this synopsis: "${synopsis}"
    ${worldSetting ? `World Setting: ${worldSetting}` : ''}
    
    Create a comprehensive and expansive cast of characters suitable for a complex ${style} novel. 
    Include:
    1. Main Characters (Protagonist, Antagonist, key allies).
    2. Secondary Characters (Friends, rivals, mentors).
    3. Minor Characters (to add flavor and world-building depth).
    
    Focus on internal conflict, distinctive voices, and relevance to the theme.
    
    IMPORTANT: Write descriptions and fields in ${language}.
  `;

  const config = {
    responseMimeType: "application/json",
    responseSchema: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          name: { type: Type.STRING },
          role: { type: Type.STRING, description: "e.g., Protagonist, Antagonist" },
          description: { type: Type.STRING, description: "Physical and personality traits" },
          arc: { type: Type.STRING, description: "How they change throughout the story" }
        },
        required: ["name", "role", "description", "arc"]
      }
    }
  };

  try {
    const response = await generateWithCache(ai, MODEL_SMART, { contents: prompt, config });
    const data = parseResponseJSON(response.text);
    return data.map((c: any) => ({ ...c, id: crypto.randomUUID() }));
  } catch (error) {
    console.warn("Smart Character Generation failed, attempting Fallback (Flash)...");
    try {
       const response = await generateWithCache(ai, MODEL_FAST, { contents: prompt, config });
       const data = parseResponseJSON(response.text);
       return data.map((c: any) => ({ ...c, id: crypto.randomUUID() }));
    } catch (fallbackError) {
       throw fallbackError;
    }
  }
};

export const writeChapter = async (
  projectData: {
    title: string;
    style: string;
    genre: string;
    characters: Character[];
    synopsis: string;
    language: string;
    styleGuide?: string;
    worldSetting?: string;
  },
  chapter: Chapter,
  previousChapterSummary: string | null
): Promise<string> => {
  const ai = getClient();

  const characterContext = projectData.characters
    .map((c) => `- ${c.name} (${c.role}): ${c.description}. Arc: ${c.arc}`)
    .join("\n");

  const prompt = `
    You are writing Chapter ${chapter.number}: "${chapter.title}" for the book "${projectData.title}".
    
    CONTEXT:
    Genre: ${projectData.genre}
    Synopsis: ${projectData.synopsis}
    Language: ${projectData.language}
    ${projectData.worldSetting ? `World Setting: ${projectData.worldSetting}` : ''}
    
    STYLE & KNOWLEDGE GUIDE (CRITICAL - FOLLOW STRICTLY):
    ${projectData.styleGuide ? projectData.styleGuide : `Style: ${projectData.style}`}
    
    CHARACTERS INVOLVED:
    ${characterContext}

    CHAPTER PLAN:
    ${chapter.summary}
    
    ${previousChapterSummary ? `PREVIOUS CONTEXT: ${previousChapterSummary}` : 'This is the first chapter.'}

    TASK:
    Write the full text for this chapter in ${projectData.language}.
    - Focus on "Show, Don't Tell".
    - deeply immersive sensory details.
    - Authentic dialogue.
    - Pacing appropriate for an award-winning novel.
    - **Integrate the specific themes, theories, or knowledge mentioned in the Style Guide.**
    - Length: Target approximately 2000-3000 words.
    - Do NOT output markdown formatting like **Chapter 1** at the start, just the prose.
    - Do NOT output any meta-commentary.
  `;

  try {
    // Try Smart model with Thinking
    const response = await generateWithCache(ai, MODEL_SMART, {
      contents: prompt,
      config: {
        thinkingConfig: { thinkingBudget: 4096 },
        maxOutputTokens: 8192,
      }
    });
    return response.text || "";
  } catch (error) {
    console.warn("Smart Chapter Write failed, attempting Fallback (Flash)...", error);
    try {
      const response = await generateWithCache(ai, MODEL_FAST, {
        contents: prompt,
        config: {
           // Standard config
        }
      });
      return response.text || "";
    } catch (fallbackError) {
      throw fallbackError;
    }
  }
};

export const continueChapter = async (
  projectData: {
    title: string;
    style: string;
    genre: string;
    characters: Character[];
    synopsis: string;
    language: string;
    styleGuide?: string;
    worldSetting?: string;
  },
  chapter: Chapter,
  currentContent: string
): Promise<string> => {
  const ai = getClient();

  // Extract the last 3000 chars to provide immediate context without overloading tokens
  const contextWindow = currentContent.slice(-3000);

  const prompt = `
    You are continuing the writing of Chapter ${chapter.number}: "${chapter.title}" for the book "${projectData.title}".
    
    CONTEXT:
    Genre: ${projectData.genre}
    Style: ${projectData.style}
    Language: ${projectData.language}
    ${projectData.worldSetting ? `World Setting: ${projectData.worldSetting}` : ''}
    
    STYLE GUIDE:
    ${projectData.styleGuide || "Consistent with previous text."}

    CHAPTER PLAN:
    ${chapter.summary}
    
    STORY SO FAR (The immediate previous text):
    "
    ${contextWindow}
    "

    TASK:
    Continue the story naturally from where the text cut off.
    - Write approximately 500-1000 words.
    - Maintain the exact same tone and voice.
    - Do NOT repeat the last sentence provided in the context.
    - Do NOT output any meta-commentary, just the new prose.
  `;

  try {
    // Attempt Smart Model for continuity
    const response = await generateWithCache(ai, MODEL_SMART, {
      contents: prompt,
      config: {
        // Lower budget for continuation to be faster, but still smart
        thinkingConfig: { thinkingBudget: 1024 }, 
        maxOutputTokens: 8192, 
      }
    });
    return response.text || "";
  } catch (error) {
    console.warn("Smart Continue failed, attempting Fallback...", error);
    try {
      const response = await generateWithCache(ai, MODEL_FAST, { contents: prompt });
      return response.text || "";
    } catch (fallbackError) {
      throw fallbackError;
    }
  }
};

export const suggestPremises = async (genre: string, language: string): Promise<string[]> => {
  const ai = getClient();
  const response = await generateWithCache(ai, MODEL_FAST, {
    contents: `Generate 3 unique, professional quality book premises for the genre: ${genre}. 1 sentence each. Write in ${language}.`,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.ARRAY,
        items: { type: Type.STRING }
      }
    }
  });
  
  if (!response.text) return [];
  try {
    return parseResponseJSON(response.text);
  } catch (e) {
    return [];
  }
};

// --- AGENTIC MUSE ---
export const askMuse = async (
  project: BookProject,
  chatHistory: MuseMessage[],
  userMessage: string,
  contextType: MuseContextType = 'story',
  activeChapterId?: string | null
): Promise<{ text: string; actionProposal?: any }> => {
  const ai = getClient();
  
  // 1. Prepare Context based on Selection
  let specificContext = "";
  const charList = project.characters.map(c => c.name).join(', ');

  if (contextType === 'story') {
    specificContext = `
      FOCUS: Full Story
      Title: ${project.title}
      Synopsis: ${project.synopsis}
      All Characters: ${charList}
      World Setting: ${project.worldSetting || 'Not defined'}
    `;
  } else if (contextType === 'chapter') {
    const chapter = project.outline.find(c => c.id === activeChapterId);
    if (chapter) {
      specificContext = `
        FOCUS: Current Chapter (${chapter.number}: ${chapter.title})
        Summary: ${chapter.summary}
        Current Content Length: ${chapter.content.length} chars
        Current Content Preview: "${chapter.content.slice(-2000)}"
      `;
    } else {
      specificContext = "FOCUS: Chapter (No specific chapter selected)";
    }
  } else if (contextType === 'characters') {
    specificContext = `
      FOCUS: Character Development
      Existing Characters: ${JSON.stringify(project.characters.map(c => ({ name: c.name, role: c.role, desc: c.description })))}
    `;
  } else if (contextType === 'world') {
    specificContext = `
      FOCUS: World Building
      World Bible: ${JSON.stringify(project.worldBible)}
      General Setting: ${project.worldSetting}
    `;
  }

  const historyText = chatHistory.slice(-6).map(m => {
    // We don't want to dump the huge JSON action props into the context prompt, just the text
    return `${m.role.toUpperCase()}: ${m.text}`;
  }).join('\n');
  
  const systemInstruction = `
    You are "The Muse", an intelligent, agentic co-author assistant.
    Your goal is to help the user write their book "${project.title}".
    
    CURRENT CONTEXT SCOPE: ${contextType.toUpperCase()}
    ${specificContext}
    
    AGENT BEHAVIOR RULES:
    1. **Clarification:** If the user asks to "add a character" or "rewrite this", do NOT do it immediately if details are vague. Ask 1-2 clarifying questions.
       - Always allow the user to say "Skip" or "Surprise me" if they don't want to answer.
       - Example: "What role should this character play? Or type 'Skip' to let me decide."
    
    2. **Action Proposals:** When you have enough information to perform a task (like creating a character or rewriting text), you MUST output a structured JSON block at the end of your response.
    
    3. **JSON Format for Actions:**
       If you are proposing an action, append this delimiter and JSON to your response:
       
       :::ACTION_JSON:::
       {
         "type": "create_character" | "rewrite_chapter",
         "label": "Short Title of Action",
         "description": "Brief description of what will change",
         "data": { ...specific data structure... }
       }
       :::END_ACTION:::

       Data Structures:
       - For "create_character": { "name": "...", "role": "...", "description": "...", "arc": "..." }
       - For "rewrite_chapter": { "content": "The full new text of the chapter..." }
    
    4. **Tone:** Encouraging, professional, creative.
  `;

  const prompt = `
    USER QUERY: "${userMessage}"
    
    RECENT CHAT HISTORY:
    ${historyText}
    
    Respond to the user. If ready to act, include the Action JSON.
  `;

  const parseOutput = (text: string | undefined) => {
    const fullText = text || "I'm sorry, I couldn't think of a response.";
    
    // Parse out the Action JSON if present
    const actionRegex = /:::ACTION_JSON:::([\s\S]*?):::END_ACTION:::/;
    const match = fullText.match(actionRegex);
    
    if (match && match[1]) {
      try {
        const actionJson = JSON.parse(match[1]);
        const cleanText = fullText.replace(match[0], "").trim();
        return { text: cleanText, actionProposal: actionJson };
      } catch (e) {
        console.error("Failed to parse Action JSON from Muse", e);
        return { text: fullText };
      }
    }
    return { text: fullText };
  };

  try {
    // Attempt 1: Smart Model with Thinking
    // Note: Thinking models are strictly 3.0 Pro or 2.5 series. 
    const response = await generateContentWithRetry(ai, MODEL_SMART, {
      contents: prompt,
      config: {
        systemInstruction,
        thinkingConfig: { thinkingBudget: 2048 } // Agentic thinking
      }
    });
    return parseOutput(response.text);

  } catch (error: any) {
    console.warn("Muse Agent failed (Smart Model), trying Fast model fallback...", error);
    try {
        // Fallback to Fast model WITHOUT thinking config
        // Using MODEL_FAST (gemini-3-flash-preview) which is generally reliable.
        const response = await generateContentWithRetry(ai, MODEL_FAST, {
            contents: prompt,
            config: {
                systemInstruction // Still pass system instructions, but no thinking budget.
            }
        });
        
        if (!response || !response.text) {
            throw new Error("Empty response from fallback model");
        }
        
        return parseOutput(response.text);
    } catch (fallbackError: any) {
        console.error("Muse Agent completely failed", fallbackError);
        const errorMsg = fallbackError.message || fallbackError.toString();
        // Return the actual error message to help the user (and us) debug
        return { 
            text: `I'm having trouble connecting to my creative engines. \n\n**Error Details:** ${errorMsg}\n\nPlease check your API Key quota or internet connection.` 
        };
    }
  }
};

export const critiqueChapter = async (
  projectData: any,
  chapter: Chapter
): Promise<CritiqueItem[]> => {
  const ai = getClient();
  const prompt = `
    Act as a senior book editor (ruthless but constructive).
    Critique the following chapter content.
    
    Context:
    Title: ${projectData.title}
    Genre: ${projectData.genre}
    Style: ${projectData.style}
    
    Chapter: ${chapter.title}
    Content (Truncated if too long):
    "${chapter.content.slice(0, 50000)}"
    
    Identify 3-5 distinct specific improvements. Focus on:
    1. Pacing issues (too slow/fast).
    2. "Show, don't tell" violations.
    3. Weak dialogue.
    4. Tone inconsistencies.
    5. Confusing descriptions.
    
    For each issue:
    - Quote the exact text (short snippet).
    - Explain the problem.
    - Suggest a concrete fix.
    
    IMPORTANT: Return JSON.
  `;

  const config = {
    responseMimeType: "application/json",
    responseSchema: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          id: { type: Type.STRING },
          type: { type: Type.STRING, enum: ['pacing', 'dialogue', 'description', 'tone', 'grammar', 'other'] },
          quote: { type: Type.STRING },
          feedback: { type: Type.STRING },
          suggestion: { type: Type.STRING }
        },
        required: ["type", "quote", "feedback", "suggestion"]
      }
    }
  };

  try {
     // Cached because critique of specific text shouldn't change
     const response = await generateWithCache(ai, MODEL_SMART, {
       contents: prompt,
       config
     });
     const data = parseResponseJSON(response.text);
     return data.map((c: any) => ({ ...c, id: crypto.randomUUID() }));
  } catch (error) {
     console.warn("Critique failed with Smart model, attempting fallback", error);
     try {
        const response = await generateWithCache(ai, MODEL_FAST, {
           contents: prompt,
           config
        });
        const data = parseResponseJSON(response.text);
        return data.map((c: any) => ({ ...c, id: crypto.randomUUID() }));
     } catch (fallbackError) {
        throw fallbackError;
     }
  }
};